import * as React from "react";
import { useNavigate } from "react-router-dom";
import { LoginForm } from "@bi-studio/shared/components";
import { useAuth } from "@bi-studio/shared/hooks";
import { Center, Loader, ActionIcon, useMantineColorScheme, Group, Box, SimpleGrid, Stack, Text, Image } from "@mantine/core";
import { Sun, Moon } from "lucide-react";
import logo from "@bi-studio/shared/assets/logo.png";
import { useAppDispatch } from "../store/hooks";
import { setCredentials } from "../store/slices/authSlice";

export default function LoginPage() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { isAuthenticated, isLoading, login, user, token } = useAuth();
    const { colorScheme, setColorScheme } = useMantineColorScheme();

    const toggleColorScheme = () => {
        setColorScheme(colorScheme === 'dark' ? 'light' : 'dark');
    };

    React.useEffect(() => {
        if (!isLoading && isAuthenticated && user && token) {
            dispatch(setCredentials({ user, token }));
            navigate("/folders");
        }
    }, [isAuthenticated, isLoading, navigate, dispatch, user, token]);

    if (isLoading) {
        return (
            <Center h="100vh">
                <Loader size="lg" />
            </Center>
        );
    }

    if (isAuthenticated) {
        return null; // Will redirect via useEffect
    }

    const handleLoginSuccess = () => {
        navigate("/folders");
    };

    return (
        <Box
            pos="relative"
            h={{ base: 'auto', md: '100vh' }}
            mih="100vh"
            className="login-page-root"
            style={{
                overflowY: 'clip', // clip ensures no scroll even if child is 100vh + epsilon
                overflowX: 'hidden'
            }}
        >
            <style dangerouslySetInnerHTML={{
                __html: `
                @media (max-width: 991px) {
                    body, html { overflow: auto !important; }
                    .login-page-root { overflow: auto !important; height: auto !important; }
                }
                @media (min-width: 992px) {
                    body, html { overflow: hidden !important; }
                    .login-page-root { overflow: hidden !important; height: 100vh !important; }
                }
            `}} />
            <Group pos="absolute" top={20} right={20} style={{ zIndex: 1000 }}>
                <ActionIcon
                    variant="default"
                    onClick={toggleColorScheme}
                    size="lg"
                    aria-label="Toggle color scheme"
                >
                    {colorScheme === 'dark' ? <Sun size={20} strokeWidth={1.5} /> : <Moon size={20} strokeWidth={1.5} />}
                </ActionIcon>
            </Group>

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={0} mih="100vh">
                {/* Left Side: Branding */}
                <Box
                    visibleFrom="md"
                    style={{
                        height: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: colorScheme === 'dark'
                            ? 'linear-gradient(135deg, var(--mantine-color-dark-8) 0%, var(--mantine-color-dark-4) 100%)'
                            : 'linear-gradient(135deg, var(--mantine-color-gray-1) 0%, var(--mantine-color-gray-3) 100%)',
                        position: 'relative'
                    }}
                >
                    <Stack align="center" gap="xl" p="xl">
                        <Box
                            p="xl"
                        >
                            <Image
                                src={typeof logo === 'string' ? logo : (logo as any).src}
                                alt="BI Studio Logo"
                                w={300}
                                style={{
                                    mixBlendMode: colorScheme === 'dark' ? 'screen' : 'multiply',
                                    filter: colorScheme === 'dark' ? 'invert(1) hue-rotate(180deg) brightness(1.2)' : 'none'
                                }}
                            />
                        </Box>
                        <Stack gap="xs" align="center">
                            <Text size="lg" fw={500} c={colorScheme === 'dark' ? 'gray.4' : 'dark.7'} ta="center" style={{ maxWidth: 400 }}>
                                Empowering your business with intelligent data analytics and real-time insights.
                            </Text>
                        </Stack>
                    </Stack>

                    {/* Subtle decorative elements */}
                    <Box
                        pos="absolute"
                        bottom={-50}
                        left={-50}
                        w={300}
                        h={300}
                        style={{
                            background: 'radial-gradient(circle, var(--mantine-color-gray-4) 0%, transparent 70%)',
                            opacity: 0.1,
                            borderRadius: '50%'
                        }}
                    />
                </Box>

                {/* Right Side: Login Form */}
                <Center p="xl" mih={{ base: 'auto', md: '100vh' }} bg={colorScheme === 'dark' ? 'var(--mantine-color-body)' : 'gray.0'}>
                    <Box w="100%" style={{ maxWidth: 450 }}>
                        <LoginForm
                            onSuccess={handleLoginSuccess}
                            onLogin={login}
                            app="web"
                            logoHiddenFrom="md"
                            fullHeight={false}
                            title="Sign in to your account"
                        />
                    </Box>
                </Center>
            </SimpleGrid>
        </Box>
    );
}
